import { Button, Flex, VStack } from '@chakra-ui/react';
import { Form, Formik, getIn } from 'formik';
import lodash from 'lodash';
import React from 'react';
import { object, string } from 'yup';

import {
  date,
  finleyAssert,
  finleyAssertUnreachable,
  typeguards,
} from '@finley-technologies/common';
import {
  typeguards as finleyTypeguards,
  Util,
} from '@finley-technologies/finley-types';

import { formatSelectOptions } from '../../../../../../common-frontend/components/Select';
import { CreditAgreementFullArtifactWipConfigType } from '../../../../types';
import {
  SelectFormField,
  TextInputFormField,
} from 'packages/common-frontend/components/FormField';

const getTypeOptions = (): Util.FormatTypeEnum[] => {
  return Object.values(Util.FormatTypeEnum);
};

/**
 * gets the formatting options for the specific type.
 *
 * does not accept `date` as a param since we accept string formatting patterns
 * using luxon library tokens/specifications.
 *
 * @param type {@link Util.FormatTypeUnion}
 */
const getFormatOptions = (
  type: Exclude<Util.FormatTypeUnion, 'date'>,
): string[] => {
  switch (type) {
    case 'currency':
      return Object.values(Util.CurrencyFormatEnum);
    case 'percentage':
      return Object.values(Util.PercentageFormatEnum);
    case 'text':
      return Object.values(Util.TextFormatEnum);
    default:
      return finleyAssertUnreachable(type);
  }
};

type ReportingOutputDocxUpdateColumnConfigFormProps = {
  basePropertyKey: string;
  columnPropertyNames?: string[];
  idx: number;
  initialValues: {
    initialColumnName: string;
    initialColumnPropertyName: string;
    initialFormat: string;
    initialType: string;
  };
  isLoading: boolean;
  onClose: () => void;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  wipConfig: CreditAgreementFullArtifactWipConfigType;
};
export const ReportingOutputDocxUpdateColumnConfigForm = ({
  basePropertyKey,
  columnPropertyNames,
  idx,
  initialValues: {
    initialColumnName,
    initialColumnPropertyName,
    initialFormat,
    initialType,
  },
  isLoading,
  onClose,
  setIsLoading,
  wipConfig,
}: ReportingOutputDocxUpdateColumnConfigFormProps) => {
  const typeOptions = formatSelectOptions(getTypeOptions(), 'title');

  return (
    <Formik
      onSubmit={async ({ columnName, columnPropertyName, format, type }) => {
        const newConfig = {
          name: columnName,
          propertyName: columnPropertyName,
          format,
          order: idx,
          type,
        };

        finleyAssert(
          finleyTypeguards.creditAgreement.isDocxColumnConfig(newConfig),
          'Options do not fit column config structure',
        );

        const configs = getIn(wipConfig.formProps.values, basePropertyKey);

        finleyAssert(
          finleyTypeguards.creditAgreement.isDocxColumnConfigs(configs),
          `Retrieved non-DOCX column configs structure for [key:${basePropertyKey}]`,
        );

        const configsCopy = lodash.cloneDeep(configs);
        configsCopy.splice(idx, 1, newConfig);

        await wipConfig.saveAndUpdateConfigCreditAgreementWip({
          wipConfig,
          propertyKey: basePropertyKey,
          updatedProperty: configsCopy,
          setIsSaveLoading: setIsLoading,
        });
        onClose();
      }}
      validationSchema={object().shape({
        columnName: string().required(),
        // validate against columnPropertyNames if provided
        columnPropertyName: string()
          .when([], {
            is: () => Array.isArray(columnPropertyNames),
            then: string().oneOf(columnPropertyNames ?? []),
            otherwise: string(),
          })
          .required(),
        type: string().required(),
        format: string()
          .when('type', {
            is: 'date',
            then: string().test({
              name: 'luxon time tokens test',
              test: (value, { createError, path }) => {
                const validator = date.validateLuxonDateFormatStringWithoutTime(
                  value ?? '',
                );
                return (
                  validator.isValid ||
                  createError({ path, message: validator.error })
                );
              },
            }),
            otherwise: string(),
          })
          .required(),
      })}
      initialValues={{
        columnName: initialColumnName,
        columnPropertyName: initialColumnPropertyName,
        format: initialFormat,
        type: initialType,
      }}
      validateOnChange={false}
    >
      {({ handleSubmit, setFieldError, setFieldValue, values: { type } }) => {
        const typeEnum = typeguards.getEnumValue<Util.FormatTypeEnum>(
          Util.FormatTypeEnum,
          type,
        );

        return (
          <Form onSubmit={handleSubmit}>
            <VStack spacing="10px">
              <TextInputFormField label="Column Name" name="columnName" />
              {columnPropertyNames ? (
                <SelectFormField
                  label="Column Property Name"
                  name="columnPropertyName"
                  options={formatSelectOptions(columnPropertyNames, 'title')}
                />
              ) : (
                <TextInputFormField
                  label="Column Property Name"
                  name="columnPropertyName"
                />
              )}

              <SelectFormField
                label="Type"
                name="type"
                options={typeOptions}
                onChange={async () => {
                  await setFieldValue('format', '');
                  setFieldError('format', undefined);
                }}
              />
              {typeEnum === 'date' ? (
                <TextInputFormField name="format" label="Date Format" />
              ) : (
                <SelectFormField
                  label="Format"
                  name="format"
                  options={formatSelectOptions(
                    getFormatOptions(typeEnum),
                    'title',
                  )}
                />
              )}
              <Flex padding-top="5px" justifyContent="center">
                <Button isLoading={isLoading} type="submit">
                  Update
                </Button>
              </Flex>
            </VStack>
          </Form>
        );
      }}
    </Formik>
  );
};
